"use client";

/**
 * P2-08 · Recently-viewed — client hook + record trigger.
 *
 * `useRecentlyViewed()` mirrors the signed `rv` cookie (parsing only the payload
 * — the server owns signature verification) and re-reads on an `rv:change`
 * event or window focus, so a view recorded on one page shows on the next.
 * `recordProductView(id)` POSTs to the write endpoint (which re-signs the
 * cookie) and notifies listeners. The list is non-PII product ids.
 */
import { useEffect, useState } from "react";

import { idsFrom, parseList } from "./recently-viewed-core";

const RV_CHANGE_EVENT = "rv:change";

function readCookieToken(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)rv=([^;]*)/);
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined; // malformed %-sequence — treat as no cookie
  }
}

/** Best-effort client parse of the token payload (no signature check). */
function idsFromToken(token: string | undefined, now: number): string[] {
  if (!token) return [];
  const dot = token.lastIndexOf(".");
  const payloadB64 = dot > 0 ? token.slice(0, dot) : token;
  try {
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    return idsFrom(parseList(json), now);
  } catch {
    return [];
  }
}

/** The visitor's recently-viewed product ids, newest-first (live). */
export function useRecentlyViewed(): string[] {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    const read = () => setIds(idsFromToken(readCookieToken(), Date.now()));
    read();
    window.addEventListener(RV_CHANGE_EVENT, read);
    window.addEventListener("focus", read);
    return () => {
      window.removeEventListener(RV_CHANGE_EVENT, read);
      window.removeEventListener("focus", read);
    };
  }, []);

  return ids;
}

/** Record a product view (best-effort); notifies `useRecentlyViewed` consumers. */
export async function recordProductView(productId: string): Promise<void> {
  try {
    await fetch("/api/recently-viewed", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ productId }),
    });
    window.dispatchEvent(new Event(RV_CHANGE_EVENT));
  } catch {
    // Recording is best-effort — never surface an error to the visitor.
  }
}
